import type { CSSProperties } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createInstantPressHandlers } from "../lib/instantPress";
import { getTileColor, isDangerTile, type Theme } from "../theme";

const TILE_LAYOUT_TRANSITION = {
  duration: 0.16,
  ease: [0.22, 1, 0.36, 1] as const
};

export type HandTileView = {
  id: string;
  value: number;
};

type HandViewProps = {
  hand: HandTileView[];
  selectedHandIndex: number | null;
  negativeSpotlightTileId: string | null;
  spotlightTileId?: string | null;
  dimNonSpotlightTiles?: boolean;
  showSelectionStyle?: boolean;
  stabilizeAnimations?: boolean;
  gameOver: boolean;
  isResolvingMove: boolean;
  hintText: string | null;
  theme: Theme;
  onSelectHandTile: (index: number) => void;
  onDismissNegativeSpotlight: () => void;
};

export function HandView({
  hand,
  selectedHandIndex,
  negativeSpotlightTileId,
  spotlightTileId = null,
  dimNonSpotlightTiles = false,
  showSelectionStyle = true,
  stabilizeAnimations = false,
  gameOver,
  isResolvingMove,
  hintText,
  theme,
  onSelectHandTile,
  onDismissNegativeSpotlight
}: HandViewProps) {
  const disabled = gameOver || isResolvingMove;
  const negativeSpotlightOpen = negativeSpotlightTileId !== null;
  const activeSpotlightTileId = negativeSpotlightTileId ?? spotlightTileId;
  const shouldDimForSpotlight = dimNonSpotlightTiles && activeSpotlightTileId !== null;
  const dismissNegativeSpotlightButtonHandlers = createInstantPressHandlers<HTMLButtonElement>(
    onDismissNegativeSpotlight
  );

  return (
    <section className={`hand-panel${negativeSpotlightOpen ? " hand-panel-negative-spotlight" : ""}`}>
      {negativeSpotlightOpen ? (
        <div className="flow-spotlight-scrim" aria-hidden="true" />
      ) : null}
      <div className="hand-hint-slot" aria-live="polite">
        <AnimatePresence initial={false}>
          {hintText !== null ? (
            <motion.p
              key={hintText}
              className="hint-text hand-hint"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {hintText}
            </motion.p>
          ) : null}
        </AnimatePresence>
      </div>
      {negativeSpotlightOpen ? (
        <div className="tile-spotlight-callout-position">
          <motion.div
            className="flow-spotlight-callout"
            role="dialog"
            aria-modal="true"
            aria-label="Negative tile"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <p className="flow-spotlight-copy">
              Negative tiles reduce the value of board tiles.
              <br />
              Reduce tiles to 0 to clear them.
            </p>
            <button
              type="button"
              className="flow-spotlight-button"
              {...dismissNegativeSpotlightButtonHandlers}
            >
              OK
            </button>
          </motion.div>
        </div>
      ) : null}
      <div
        className={`hand-row${disabled ? " hand-row-disabled" : ""}${
          negativeSpotlightOpen ? " hand-row-negative-spotlight" : ""
        }`}
        style={{ "--hand-slot-count": Math.max(hand.length, 1) } as CSSProperties}
      >
        <AnimatePresence initial={false}>
          {hand.map((tile, index) => {
            const selected = showSelectionStyle && selectedHandIndex === index;
            const color = getTileColor(tile.value);
            const isNegativeSpotlight = negativeSpotlightTileId === tile.id;
            const isTutorialSpotlight = spotlightTileId === tile.id;
            const isSpotlightTile = isNegativeSpotlight || isTutorialSpotlight;
            const isSpotlightMuted = shouldDimForSpotlight && !isSpotlightTile;

            return (
              <motion.button
                key={tile.id}
                type="button"
                layout={stabilizeAnimations ? false : "position"}
                initial={
                  stabilizeAnimations ? false : { opacity: 0, scale: 0.94, y: 10 }
                }
                animate={{
                  opacity: isSpotlightMuted ? 0.28 : 1,
                  scale: selected ? 1.08 : 1,
                  y: selected ? -5 : 0
                }}
                exit={
                  stabilizeAnimations
                    ? { opacity: 0, scale: 1, y: 0, transition: { duration: 0 } }
                    : { opacity: 0, scale: 0.94, y: 8, transition: { duration: 0.12 } }
                }
                transition={{
                  layout: stabilizeAnimations ? { duration: 0 } : TILE_LAYOUT_TRANSITION,
                  scale: { duration: 0 },
                  y: { duration: 0 },
                  opacity: { duration: stabilizeAnimations ? 0 : 0.12 }
                }}
                className={`tile tile-hand tile-surface-${theme}${
                  selected ? " tile-selected tile-hand-selected" : ""
                }${isDangerTile(tile.value) ? " tile-danger" : ""}${
                  isSpotlightTile ? " tile-mechanic-spotlight" : ""
                }${
                  isSpotlightMuted ? " tile-spotlight-muted" : ""
                }`}
                style={{ "--tile-color": color.hex } as CSSProperties}
                onClick={() => onSelectHandTile(index)}
                disabled={disabled}
                aria-label={`Select hand tile ${index + 1} with value ${tile.value}`}
              >
                <span className="tile-number">{tile.value}</span>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </section>
  );
}
